/* The site-wide row of numbers, belonging to the Overview panel alone; every other page
 * builds its own from its own data (see `Tiles`). Each figure is paired with the same
 * measure over the preceding window, so it reads as a direction rather than a level, and
 * each carries the panel it is broken down on — the Overview is a map of the site, so a
 * figure that looks wrong has to be the way to where it can be read properly.
 */

import {
  MessagesSquare,
  SearchX,
  ShieldAlert,
  Star,
  Target,
  ThumbsUp,
  Ticket,
  Users,
} from 'lucide-react';

import { TileRow, type TileSpec } from './Tiles';
import { num, pct, plural, rel } from '../lib/format';
import { tabLabelOf } from '../nav';
import { useApp } from '../state/app';
import type { Overview } from '../api/types';

/** The overview is handed in rather than fetched here: the panel has already waited for it,
 *  and a second request for the same endpoint settles after the page is drawn, so the row
 *  would open as a hole the height of itself and shove the cards below it down on arrival. */
export function StatTiles({ data }: { data: Overview }) {
  const { simple } = useApp();
  const c = data.current;
  const p = data.previous;

  /** Where a figure is broken down, named as the rail names it. Only tabs this account has:
   *  a tile pointing at a panel its role is refused would land it back on the fallback. */
  const on = (id: string) => ({ to: `/${id}`, toLabel: tabLabelOf(id, simple) });

  // Four tiles rather than eight for an admin, each a plain count. The four dropped appear
  // as the checks below, where each is stated as a sentence with a verdict.
  const tiles: TileSpec[] = simple
    ? [
        {
          label: 'Questions asked',
          value: num(c.turns),
          delta: rel(c.turns, p.turns),
          hint: `across ${plural(c.conversations, 'conversation')}`,
          icon: <MessagesSquare className="h-4 w-4" />,
          ...on('adoption'),
        },
        {
          label: 'People asking',
          value: num(c.users),
          delta: rel(c.users, p.users),
          hint: c.users
            ? c.anonymous_turns
              ? `signed-in people; ${plural(c.anonymous_turns, 'question')} came from outside a login and cannot be counted as people`
              : 'signed-in people'
            : 'nobody signed in yet',
          icon: <Users className="h-4 w-4" />,
          ...on('adoption'),
        },
        {
          label: 'Answers people liked',
          value: pct(c.helpful_rate),
          delta: rel(c.helpful_rate, p.helpful_rate),
          hint: c.feedback_n
            ? `out of ${plural(c.feedback_n, 'rating')}`
            : 'nobody has rated an answer yet',
          icon: <ThumbsUp className="h-4 w-4" />,
          ...on('quality'),
        },
        {
          label: 'Answered without a matching document',
          value: pct(c.low_confidence_rate),
          delta: rel(c.low_confidence_rate, p.low_confidence_rate),
          hint: 'the assistant replied, but nothing in your documents was really about the question',
          lowerIsBetter: true,
          icon: <SearchX className="h-4 w-4" />,
          ...on('gaps'),
        },
      ]
    : [
        {
          label: 'Questions asked',
          value: num(c.turns),
          delta: rel(c.turns, p.turns),
          hint: `across ${plural(c.conversations, 'conversation')}`,
          icon: <MessagesSquare className="h-4 w-4" />,
          ...on('adoption'),
        },
        {
          // Signed-in people only. The hint carries the questions asked without a token,
          // which cannot be attributed to anybody.
          label: 'People',
          value: num(c.users),
          delta: rel(c.users, p.users),
          hint: c.users
            ? c.anonymous_turns
              ? `signed-in users; ${num(c.anonymous_turns)} questions asked without a login are not counted here`
              : 'signed-in users'
            : 'no signed-in users yet',
          icon: <Users className="h-4 w-4" />,
          ...on('adoption'),
        },
        {
          label: 'Rated helpful',
          value: pct(c.helpful_rate),
          delta: rel(c.helpful_rate, p.helpful_rate),
          hint: c.feedback_n ? `out of ${plural(c.feedback_n, 'rating')}` : 'no ratings yet',
          icon: <ThumbsUp className="h-4 w-4" />,
          ...on('quality'),
        },
        {
          label: 'Ratings given',
          value: pct(c.rating_coverage, 1),
          hint: 'share of answers rated — low means the quality numbers are thin',
          icon: <Star className="h-4 w-4" />,
          ...on('quality'),
        },
        {
          // The field is named `confidence` on the wire, but the number is the retrieval
          // score, not a probability that the answer was right — no label here may imply
          // otherwise.
          label: 'Weak evidence',
          value: pct(c.low_confidence_rate),
          delta: rel(c.low_confidence_rate, p.low_confidence_rate),
          hint: 'answers built on loosely-related docs — a place to look, not a verdict',
          lowerIsBetter: true,
          icon: <SearchX className="h-4 w-4" />,
          ...on('gaps'),
        },
        {
          label: 'Escalation rate',
          value: pct(c.escalation_rate),
          delta: rel(c.escalation_rate, p.escalation_rate),
          hint: 'tickets per conversation — should fall over time',
          lowerIsBetter: true,
          icon: <Ticket className="h-4 w-4" />,
          ...on('tickets'),
        },
        {
          label: 'Failed answers',
          value: pct(c.failure_rate, 1),
          delta: rel(c.failure_rate, p.failure_rate),
          hint: 'a technical fault, not a missing document',
          lowerIsBetter: true,
          icon: <ShieldAlert className="h-4 w-4" />,
          ...on('health'),
        },
        {
          label: 'Avg evidence score',
          value: num(c.avg_confidence, 2),
          delta: rel(c.avg_confidence, p.avg_confidence),
          hint: 'mean similarity of the best documentation found — not a correctness rate',
          icon: <Target className="h-4 w-4" />,
          ...on('documents'),
        },
      ];

  return <TileRow tiles={tiles} />;
}
